/**
 * Manually publish a news article (Admin) — bypasses the RSS pipeline for
 * Weather/War/Disaster alerts and "News You Can Use" curated updates.
 */
import { useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Send, Link as LinkIcon, Image as ImageIcon, Trash2 } from "lucide-react";

const CATEGORIES = [
  "Weather", "War", "Disasters", "News You Can Use",
  "World", "Politics", "Business", "Technology", "Science", "Health",
  "Environment", "Entertainment", "Sports", "Good News", "Odd News",
];

function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
  });
}

export default function AdminNewsCreate({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [category, setCategory] = useState("News You Can Use");
  const [country, setCountry] = useState("Global");
  const [importance, setImportance] = useState(4);
  const [imageUrl, setImageUrl] = useState("");
  const [images, setImages] = useState([]);
  const [links, setLinks] = useState([{ title: "", url: "" }]);
  const [publishedAt, setPublishedAt] = useState("");
  const [sourceName, setSourceName] = useState("Social Pulse Editorial");
  const [sourceUrl, setSourceUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const addImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error("< 4 MB");
    const data = await fileToDataUrl(file);
    setImages((p) => [...p, data]);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title || title.length < 3) return toast.error("Title required");
    if (!summary || summary.length < 10) return toast.error("Description required");
    const validLinks = links.filter((l) => l.url && l.title);
    setSubmitting(true);
    try {
      await http.post("/admin/news", {
        title: title.trim(),
        summary: summary.trim(),
        category, country, importance,
        image_url: imageUrl || (images[0] || null),
        images,
        links: validLinks,
        published_at: publishedAt || null,
        source_name: sourceName || null,
        source_url: sourceUrl || null,
      });
      toast.success("Article published");
      setTitle(""); setSummary(""); setImageUrl(""); setImages([]); setLinks([{ title: "", url: "" }]);
      setPublishedAt(""); setSourceUrl("");
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Publish failed");
    } finally { setSubmitting(false); }
  };

  const inputClass = "w-full px-3 py-2 rounded-lg border bg-transparent text-sm";
  const inputStyle = { borderColor: "var(--wofy-border)" };

  return (
    <div className="mb-6" data-testid="admin-news-manual-create">
      <button
        onClick={() => setOpen(!open)}
        data-testid="admin-news-manual-toggle"
        className="wofy-btn-primary text-sm"
      >
        <Plus className="w-4 h-4" /> {open ? "Close manual publisher" : "Publish news manually"}
      </button>

      {open && (
        <form onSubmit={submit} className="wofy-card mt-4 space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass} style={inputStyle} data-testid="anm-category">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Country (or Global)" className={inputClass} style={inputStyle} />
          </div>
          <input required data-testid="anm-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Headline *" className={inputClass} style={inputStyle} />
          <textarea required data-testid="anm-summary" value={summary} onChange={(e) => setSummary(e.target.value)} rows={6} maxLength={5000} placeholder="Full description *" className={inputClass} style={inputStyle} />

          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-mono uppercase tracking-wider block mb-1">Importance (1–5)</label>
              <input type="number" min="1" max="5" value={importance} onChange={(e) => setImportance(parseInt(e.target.value, 10))} className={inputClass} style={inputStyle} />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider block mb-1">Publish date/time</label>
              <input type="datetime-local" value={publishedAt} onChange={(e) => setPublishedAt(e.target.value)} className={inputClass} style={inputStyle} />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider block mb-1">Source URL</label>
              <input type="url" value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="https://…" className={inputClass} style={inputStyle} />
            </div>
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-wider block mb-1"><ImageIcon className="w-3 h-3 inline mr-1" /> Cover image URL</label>
            <input type="url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://…" className={inputClass} style={inputStyle} />
            <div className="mt-2 text-xs opacity-60">…or upload additional images:</div>
            <input type="file" accept="image/*" onChange={addImage} className="text-xs mt-1" />
            {images.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {images.map((img, i) => (
                  <div key={i} className="relative">
                    <img src={img} alt="" className="w-20 h-20 rounded-lg object-cover border" />
                    <button type="button" onClick={() => setImages((p) => p.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 rounded-full bg-red-500 text-white w-5 h-5 grid place-items-center">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-wider block mb-1"><LinkIcon className="w-3 h-3 inline mr-1" /> Related links</label>
            {links.map((l, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input value={l.title} onChange={(e) => setLinks((p) => p.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} placeholder="Link label" className={inputClass} style={inputStyle} />
                <input type="url" value={l.url} onChange={(e) => setLinks((p) => p.map((x, j) => j === i ? { ...x, url: e.target.value } : x))} placeholder="https://…" className={inputClass} style={inputStyle} />
                <button type="button" onClick={() => setLinks((p) => p.filter((_, j) => j !== i))} className="wofy-btn-ghost text-xs" style={{ color: "#B23A3A" }} aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button type="button" onClick={() => setLinks((p) => [...p, { title: "", url: "" }])} className="wofy-btn-ghost text-xs">
              <Plus className="w-3 h-3" /> Add link
            </button>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="wofy-btn-ghost">Cancel</button>
            <button type="submit" data-testid="anm-submit" disabled={submitting} className="wofy-btn-primary">
              <Send className="w-4 h-4" /> {submitting ? "Publishing…" : "Publish now"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
